/**
 * Uber Eats authentication – follows official guide:
 * https://developer.uber.com/docs/eats/guides/authentication
 *
 * Two flows:
 * - Authorization code: store discovery & integration activation only (scope: eats.pos_provisioning)
 * - Client credentials: Store, Menu, Order, Reporting (scopes: eats.store eats.order); cache token, 100 req/hour
 *
 * Production: auth.uber.com, api.uber.com. Sandbox: sandbox-login.uber.com, test-api.uber.com.
 *
 * In the browser, token requests go through the Firebase Cloud Function "uberToken" to avoid CORS
 * (Uber's token endpoint does not allow cross-origin requests). Set UBER_CLIENT_ID and UBER_CLIENT_SECRET
 * in Firebase Functions config or env for the proxy to work.
 *
 * Option A: Prefer callable getUberClientToken (caches token in Firestore server-side) to stay under 100 req/hour.
 */

import { config } from './config';

const AUTH_BASE_SANDBOX = 'https://sandbox-login.uber.com';
const AUTH_BASE_PROD = 'https://auth.uber.com';
const API_BASE_SANDBOX = 'https://test-api.uber.com';
const API_BASE_PROD = 'https://api.uber.com';

function getAuthBase(): string {
  return config.uber.useSandbox ? AUTH_BASE_SANDBOX : AUTH_BASE_PROD;
}

function getApiBase(): string {
  return config.uber.useSandbox ? API_BASE_SANDBOX : API_BASE_PROD;
}

/** URL of the Firebase Cloud Function that proxies Uber token requests (avoids CORS in browser). */
function getUberTokenProxyUrl(): string {
  const projectId = config.firebase.projectId;
  const region = 'us-central1';
  if (!projectId) return '';
  return `https://${region}-${projectId}.cloudfunctions.net/uberToken`;
}

const isBrowser = typeof window !== 'undefined';

async function signedInAuthHeader(): Promise<Record<string, string>> {
  const { getFirebaseAuth } = await import('./firebase');
  const user = getFirebaseAuth().currentUser;
  if (!user) {
    throw new Error('Must be signed in to request an Uber token.');
  }
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

export const UBER_OAUTH_STATE = 'uber_eats_pos';

/**
 * Build the authorization URL for Authorization Code flow (store discovery & integration activation).
 * Per docs: scope must be eats.pos_provisioning only. Redirects back to redirect_uri with ?code=...
 */
export function getUberAuthorizationUrl(state: string = UBER_OAUTH_STATE): string {
  const clientId = config.uber.clientId;
  const redirectUri = config.uber.redirectUri;
  const scopes = config.uber.authCodeScopes.join(' ');
  if (!clientId || !redirectUri) {
    throw new Error('Uber Eats: VITE_UBER_CLIENT_ID and VITE_UBER_REDIRECT_URI must be set.');
  }
  const base = getAuthBase();
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: scopes,
    state,
  });
  return `${base}/oauth/v2/authorize?${params.toString()}`;
}

export interface UberTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number; // seconds
  refresh_token?: string;
  scope?: string;
}

/**
 * Exchange the authorization code (from callback ?code=) for access_token and refresh_token (Step 4).
 * Call this when the app loads with ?code= in the URL.
 * In the browser, uses the uberToken Cloud Function to avoid CORS.
 */
export async function exchangeCodeForToken(code: string): Promise<UberTokenResponse> {
  const redirectUri = config.uber.redirectUri;
  if (!redirectUri) {
    throw new Error('Uber Eats: VITE_UBER_REDIRECT_URI must be set (or set UBER_REDIRECT_URI in Firebase Functions for the proxy).');
  }
  if (isBrowser && getUberTokenProxyUrl()) {
    const proxyUrl = getUberTokenProxyUrl();
    const res = await fetch(proxyUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await signedInAuthHeader()) },
      body: JSON.stringify({
        grant_type: 'authorization_code',
        code,
        redirect_uri: redirectUri,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error((data.error as string) || `Uber token exchange failed: ${res.status}`);
    }
    return data as UberTokenResponse;
  }
  const clientId = config.uber.clientId;
  const clientSecret = config.uber.clientSecret;
  if (!clientId || !clientSecret) {
    throw new Error('Uber Eats: VITE_UBER_CLIENT_ID and VITE_UBER_CLIENT_SECRET must be set (or configure the uberToken Cloud Function).');
  }
  const base = getAuthBase();
  const body = new URLSearchParams({
    client_id: clientId,
    client_secret: clientSecret,
    grant_type: 'authorization_code',
    redirect_uri: redirectUri,
    code,
  });
  const res = await fetch(`${base}/oauth/v2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Uber token exchange failed: ${res.status} ${text}`);
  }
  return res.json() as Promise<UberTokenResponse>;
}

/**
 * Refresh an expired access token using the stored refresh_token (if supported by Uber for this grant type).
 */
export async function refreshUberAccessToken(refreshToken: string): Promise<UberTokenResponse> {
  const clientId = config.uber.clientId;
  const clientSecret = config.uber.clientSecret;
  if (!clientId || !clientSecret) {
    throw new Error('Uber Eats: VITE_UBER_CLIENT_ID and VITE_UBER_CLIENT_SECRET must be set.');
  }
  const base = getAuthBase();
  const body = new URLSearchParams({
    client_id: clientId,
    client_secret: clientSecret,
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
  });
  const res = await fetch(`${base}/oauth/v2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Uber token refresh failed: ${res.status} ${text}`);
  }
  return res.json() as Promise<UberTokenResponse>;
}

/**
 * Client credentials token with server-side cache (Option A).
 * In the browser with Firebase projectId, calls the getUberClientToken callable so the token is
 * cached in Firestore and reused until expiry (avoids 100 token/hour limit). Otherwise falls back
 * to getClientCredentialsToken (proxy or direct).
 */
export async function getCachedClientCredentialsToken(overrides?: { clientId?: string; clientSecret?: string }): Promise<UberTokenResponse> {
  if (isBrowser && config.firebase.projectId) {
    try {
      const { getFirebaseFunctions } = await import('./firebase');
      const { httpsCallable } = await import('firebase/functions');
      const fn = getFirebaseFunctions();
      const getUberClientToken = httpsCallable<Record<string, never>, UberTokenResponse>(fn, 'getUberClientToken');
      const res = await getUberClientToken({});
      return res.data;
    } catch (err) {
      const msg = (err as { message?: string })?.message ?? String(err);
      if (msg.includes('unauthenticated') || msg.includes('Must be signed in')) throw err;
      if (msg.includes('failed-precondition') || msg.includes('not configured')) throw err;
      throw new Error(msg);
    }
  }
  return getClientCredentialsToken(overrides);
}

/**
 * Client credentials flow – for regular API operations (Store, Menu, Order, Reporting).
 * Per docs: use grant_type=client_credentials, scope=eats.store eats.order.
 * Tokens valid 30 days; rate limit 100 token requests/hour – cache and reuse.
 * In the browser, uses the uberToken Cloud Function (credentials must be set in Firebase Functions).
 * Outside the browser (or when proxy not available), can use overrides or env (VITE_UBER_*).
 */
export async function getClientCredentialsToken(overrides?: { clientId?: string; clientSecret?: string }): Promise<UberTokenResponse> {
  const scopes = config.uber.clientCredentialsScopes.join(' ');
  if (isBrowser && getUberTokenProxyUrl()) {
    const proxyUrl = getUberTokenProxyUrl();
    const res = await fetch(proxyUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await signedInAuthHeader()) },
      body: JSON.stringify({
        grant_type: 'client_credentials',
        scope: scopes,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error((data.error as string) || `Uber client_credentials failed: ${res.status}`);
    }
    return data as UberTokenResponse;
  }
  const clientId = overrides?.clientId?.trim() || config.uber.clientId;
  const clientSecret = overrides?.clientSecret != null ? overrides.clientSecret : config.uber.clientSecret;
  if (!clientId || !clientSecret) {
    throw new Error('Uber Eats: set Client ID and Client Secret in Settings (or use VITE_UBER_CLIENT_ID and VITE_UBER_CLIENT_SECRET in .env). For browser, set UBER_CLIENT_ID and UBER_CLIENT_SECRET in Firebase Functions.');
  }
  const base = getAuthBase();
  const body = new URLSearchParams({
    client_id: clientId,
    client_secret: String(clientSecret),
    grant_type: 'client_credentials',
    scope: scopes,
  });
  const res = await fetch(`${base}/oauth/v2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Uber client_credentials token failed: ${res.status} ${text}`);
  }
  return res.json() as Promise<UberTokenResponse>;
}

/**
 * Test the connection by calling Uber Eats stores API with the given access token.
 * Use either an authorization_code token (store discovery) or a client_credentials token (regular ops).
 * Returns list of stores or throws on failure.
 * Note: From the browser this hits CORS; use testUberConnectionViaCallable() instead.
 */
export async function testUberConnection(accessToken: string): Promise<{ stores?: unknown[]; next_key?: string }> {
  const base = getApiBase();
  const res = await fetch(`${base}/v1/eats/stores?limit=5`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Accept-Encoding': 'gzip',
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Uber Eats API error: ${res.status} ${text}`);
  }
  return res.json();
}

/**
 * Test Uber Eats connection via the testUberEatsConnection callable (server-side).
 * Use this in the browser to avoid CORS; the function gets a token and calls Uber's stores API.
 */
export async function testUberConnectionViaCallable(): Promise<{ stores?: unknown[]; next_key?: string }> {
  if (!isBrowser || !config.firebase.projectId) {
    throw new Error('testUberConnectionViaCallable is for browser only with Firebase projectId');
  }
  const { getFirebaseFunctions } = await import('./firebase');
  const { httpsCallable } = await import('firebase/functions');
  const fn = getFirebaseFunctions();
  const testUberEatsConnection = httpsCallable<Record<string, never>, { stores: unknown[]; next_key?: string }>(fn, 'testUberEatsConnection');
  const res = await testUberEatsConnection({});
  return res.data;
}

/**
 * Sync POS menu to Uber Eats via the syncUberEatsMenu callable (server-side).
 * Requires Store ID to be set in Settings. Returns { success, categoriesCount, itemsCount }.
 */
export async function syncUberEatsMenuViaCallable(): Promise<{
  success: boolean;
  categoriesCount: number;
  itemsCount: number;
}> {
  if (!isBrowser || !config.firebase.projectId) {
    throw new Error('syncUberEatsMenuViaCallable is for browser only with Firebase projectId');
  }
  const { getFirebaseFunctions } = await import('./firebase');
  const { httpsCallable } = await import('firebase/functions');
  const fn = getFirebaseFunctions();
  const syncUberEatsMenu = httpsCallable<Record<string, never>, { success: boolean; categoriesCount: number; itemsCount: number }>(
    fn,
    'syncUberEatsMenu'
  );
  const res = await syncUberEatsMenu({});
  return res.data;
}

/**
 * Accept an Uber Eats pending order via callable. Creates POS order and calls Uber Accept.
 * orderId = Uber order UUID (same as Firestore uber_pending_orders doc id).
 */
export async function acceptUberEatsOrder(orderId: string): Promise<{ success: boolean; orderId: string }> {
  if (!isBrowser || !config.firebase.projectId) {
    throw new Error('acceptUberEatsOrder is for browser only with Firebase projectId');
  }
  const { getFirebaseFunctions } = await import('./firebase');
  const { httpsCallable } = await import('firebase/functions');
  const fn = getFirebaseFunctions();
  const accept = httpsCallable<{ orderId: string }, { success: boolean; orderId: string }>(fn, 'acceptUberEatsOrder');
  const res = await accept({ orderId });
  return res.data;
}

/**
 * Mark an Uber Eats order as ready for pickup via callable.
 * Moves the order to "Ready for pick up" in the Uber store. orderId = Uber order UUID.
 */
export async function markUberEatsOrderReady(orderId: string): Promise<{ success: boolean }> {
  if (!isBrowser || !config.firebase.projectId) {
    throw new Error('markUberEatsOrderReady is for browser only with Firebase projectId');
  }
  const { getFirebaseFunctions } = await import('./firebase');
  const { httpsCallable } = await import('firebase/functions');
  const fn = getFirebaseFunctions();
  const ready = httpsCallable<{ orderId: string }, { success: boolean }>(fn, 'markUberEatsOrderReady');
  const res = await ready({ orderId });
  return res.data;
}

/**
 * Deny an Uber Eats pending order via callable.
 * orderId = Uber order UUID. reasonCode = e.g. OTHER, ITEM_AVAILABILITY, POS_OFFLINE.
 */
export async function denyUberEatsOrder(
  orderId: string,
  reason?: { code?: string; explanation?: string }
): Promise<{ success: boolean }> {
  if (!isBrowser || !config.firebase.projectId) {
    throw new Error('denyUberEatsOrder is for browser only with Firebase projectId');
  }
  const { getFirebaseFunctions } = await import('./firebase');
  const { httpsCallable } = await import('firebase/functions');
  const fn = getFirebaseFunctions();
  const deny = httpsCallable<
    { orderId: string; reasonCode?: string; reasonExplanation?: string },
    { success: boolean }
  >(fn, 'denyUberEatsOrder');
  const res = await deny({
    orderId,
    reasonCode: reason?.code,
    reasonExplanation: reason?.explanation,
  });
  return res.data;
}

/**
 * Report an issue with items on an accepted Uber Eats order (e.g. out of stock).
 * This calls the reportUberOrderIssue callable, which in turn talks to Uber's Order Suite API.
 */
export async function reportUberOrderIssue(
  orderId: string,
  payload: { items: { title?: string; quantity: number; cartItemId?: string }[]; reasonCode: string; explanation?: string }
): Promise<{ success: boolean }> {
  if (!isBrowser || !config.firebase.projectId) {
    throw new Error('reportUberOrderIssue is for browser only with Firebase projectId');
  }
  const { getFirebaseFunctions } = await import('./firebase');
  const { httpsCallable } = await import('firebase/functions');
  const fn = getFirebaseFunctions();
  const callable = httpsCallable<
    { orderId: string; items: { title?: string; quantity: number; cartItemId?: string }[]; reasonCode: string; explanation?: string },
    { success: boolean }
  >(fn, 'reportUberOrderIssue');
  const res = await callable({
    orderId,
    items: payload.items,
    reasonCode: payload.reasonCode,
    explanation: payload.explanation,
  });
  return res.data;
}

/**
 * Reconcile pending Uber orders with Uber-side status.
 * Helps keep POS in sync when staff actions happen on Uber tablet.
 */
export async function reconcileUberPendingOrders(): Promise<{
  success: boolean;
  checked: number;
  movedToOrders: number;
  cancelled: number;
  unchanged: number;
}> {
  if (!isBrowser || !config.firebase.projectId) {
    throw new Error('reconcileUberPendingOrders is for browser only with Firebase projectId');
  }
  const { getFirebaseFunctions } = await import('./firebase');
  const { httpsCallable } = await import('firebase/functions');
  const fn = getFirebaseFunctions();
  const callable = httpsCallable<
    Record<string, never>,
    { success: boolean; checked: number; movedToOrders: number; cancelled: number; unchanged: number }
  >(fn, 'reconcileUberPendingOrders');
  const res = await callable({});
  return res.data;
}

/**
 * Check if Uber OAuth (authorization code) is configured (client_id and redirect_uri).
 */
export function isUberOAuthConfigured(): boolean {
  return !!(config.uber.clientId && config.uber.redirectUri);
}

/**
 * Check if client credentials flow can be used (client_id and client_secret in env/form).
 */
export function isUberCredentialsConfigured(): boolean {
  return !!(config.uber.clientId && config.uber.clientSecret);
}

/**
 * Check if the Uber token proxy (Cloud Function) is available for browser requests.
 * When true, Test Connection and token exchange use the proxy and do not require credentials in the frontend.
 */
export function isUberTokenProxyAvailable(): boolean {
  return isBrowser && !!config.firebase.projectId && !!getUberTokenProxyUrl();
}
